/**
 * Treatment variant hero for the cfind_templates_blended_hero_v7 Switcheroo experiment.
 *
 * "Blended Hero" (Treatment C, split layout):
 *   - Left column: "Templates for Confluence" heading + subhead + search bar
 *     (left-aligned, injected by the page via `searchSlot`) + example-query hint.
 *   - Right column: white marketing/setup card with a value headline, three setup
 *     steps, a "Get it free" primary CTA and a "Learn more →" secondary link.
 *
 * The search bar differs between the unified (context-driven) and relay
 * (prop-driven) templates, so it is passed in as `searchSlot` rather than
 * rendered internally — keeping this component template-agnostic.
 *
 * @see https://switcheroo.atlassian.com/ui/gates/e6541082-fbec-4dc7-8d9d-d4b4c48e0322/key/cfind_templates_blended_hero_v7
 */

import { type ReactNode } from 'react';

import { css } from '@compiled/react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';

import { media } from '@atlaskit/primitives/responsive';
import { token } from '@atlaskit/tokens';

import { Button } from '@/libraries/design/components/button/src/button';
import { Link } from '@/libraries/design/components/link/src/link.tsx';
import { wacToken } from '@/libraries/design/foundations';

const messages = defineMessages({
	heading: {
		id: 'confluence.templates.hero.blended.heading',
		defaultMessage: 'Templates for Confluence',
		description: 'Heading for the blended templates hero experiment treatment',
	},
	subhead: {
		id: 'confluence.templates.hero.blended.subhead',
		defaultMessage:
			'Find a ready-made template for any job — project plans, meeting notes, product specs, and 100+ more.',
		description: 'Subhead beneath the blended templates hero heading',
	},
	searchHint: {
		id: 'confluence.templates.hero.blended.search-hint',
		defaultMessage: 'Try “product requirements” or “weekly meeting notes”',
		description: 'Example-query hint shown below the search bar',
	},
	cardHeadline: {
		id: 'confluence.templates.hero.blended.card-headline',
		defaultMessage: 'The AI workspace that works with you',
		description: 'Headline on the blended hero marketing card',
	},
	cardStep1: {
		id: 'confluence.templates.hero.blended.card-step-1',
		defaultMessage: 'Get a free workspace in seconds',
		description: 'First setup step on the blended hero card',
	},
	cardStep2: {
		id: 'confluence.templates.hero.blended.card-step-2',
		defaultMessage: '100+ templates ready when you land',
		description: 'Second setup step on the blended hero card',
	},
	cardStep3: {
		id: 'confluence.templates.hero.blended.card-step-3',
		defaultMessage: 'Customize with AI and collaborate live',
		description: 'Third setup step on the blended hero card',
	},
	primaryCta: {
		id: 'confluence.templates.hero.blended.cta-primary',
		defaultMessage: 'Get it free',
		description: 'Primary CTA button text on the blended hero card',
	},
	secondaryCta: {
		id: 'confluence.templates.hero.blended.cta-secondary',
		defaultMessage: 'Learn more',
		description: 'Secondary CTA link text on the blended hero card',
	},
});

export type TemplateHeroBlendedV7Props = {
	/**
	 * The search bar to render, left-aligned, in the left column. The unified and
	 * relay templates supply different `TemplateFuzzySearch` instances, so it is
	 * injected here rather than rendered internally.
	 */
	searchSlot?: ReactNode;
};

const TemplateHeroBlendedV7 = ({ searchSlot }: TemplateHeroBlendedV7Props) => {
	const intl = useIntl();
	const secondaryCtaText = intl.formatMessage(messages.secondaryCta);

	return (
		<div css={containerStyles}>
			<header css={heroStyles} data-testid="template-hero-blended-v7">
				<div css={leftColumnStyles}>
					<h1 css={headingStyles}>
						<FormattedMessage {...messages.heading} />
					</h1>
					<p css={subheadStyles}>
						<FormattedMessage {...messages.subhead} />
					</p>
					{searchSlot ? (
						<div css={searchSlotStyles} data-testid="template-hero-blended-v7-search">
							{searchSlot}
						</div>
					) : null}
					<span css={searchHintStyles}>
						<FormattedMessage {...messages.searchHint} />
					</span>
				</div>
				<div css={rightColumnStyles}>
					<div css={cardStyles}>
						<h2 css={cardHeadlineStyles}>
							<FormattedMessage {...messages.cardHeadline} />
						</h2>
						<ol css={cardStepsStyles}>
							<li css={cardStepStyles}>
								<span css={cardStepNumberStyles}>1</span>
								<FormattedMessage {...messages.cardStep1} />
							</li>
							<li css={cardStepStyles}>
								<span css={cardStepNumberStyles}>2</span>
								<FormattedMessage {...messages.cardStep2} />
							</li>
							<li css={cardStepStyles}>
								<span css={cardStepNumberStyles}>3</span>
								<FormattedMessage {...messages.cardStep3} />
							</li>
						</ol>
						<div css={cardCtaContainerStyles}>
							<Button
								css={primaryCtaStyles}
								variant="primary_alt"
								href="/try/cloud/signup?bundle=confluence"
								data-testid="template-hero-blended-v7-cta-primary"
								dataEventProps={{
									'data-product-key': 'confluence',
									'data-event': 'clicked',
									'data-event-component': 'button',
									'data-event-container': 'heroSection',
									'data-label': 'Get it free',
									'data-label-english': 'Get it free',
									'data-track-event': 'confluence-pt.templates.blended-hero.cta-primary',
								}}
							>
								<FormattedMessage {...messages.primaryCta} />
							</Button>
							<Link
								text={secondaryCtaText}
								aria-label={secondaryCtaText}
								href="/software/confluence"
								type="primary"
								showArrow
								dataEventProps={{
									'data-product-key': 'confluence',
									'data-event': 'clicked',
									'data-event-component': 'linkButton',
									'data-event-container': 'heroSection',
									'data-label': 'Learn more',
									'data-label-english': 'Learn more',
									'data-track-event': 'confluence-pt.templates.blended-hero.cta-secondary',
								}}
								anchorProps={{
									'data-testid': 'template-hero-blended-v7-cta-secondary',
								}}
							/>
						</div>
					</div>
				</div>
			</header>
		</div>
	);
};

TemplateHeroBlendedV7.displayName = 'TemplateHeroBlendedV7';
export { TemplateHeroBlendedV7 };

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const containerStyles = css({
	display: 'flex',
	justifyContent: 'center',
});

const heroStyles = css({
	flex: '1',
	display: 'flex',
	flexDirection: 'row',
	alignItems: 'center',
	borderRadius: '20px',
	marginBlock: '45px',
	padding: '48px 56px',
	width: '100%',
	maxWidth: '1304px',
	background: `url(https://wac-cdn.atlassian.com/misc-assets/webp-images/bg_atl_cloud_hero_small.svg) center/cover no-repeat`,
	color: wacToken('color.neutral.light0'),
	minHeight: '298px',
	marginInline: '78px',
	gap: '48px',
	[media.below.lg]: {
		marginInline: '32px',
		padding: '40px 32px',
		gap: '32px',
	},
	[media.below.sm]: {
		flexDirection: 'column',
		marginInline: '24px',
		padding: '32px 24px',
		gap: '24px',
	},
	[media.below.xs]: {
		marginInline: '16px',
	},
});

const leftColumnStyles = css({
	flex: '1 1 auto',
	minWidth: '0',
	display: 'flex',
	flexDirection: 'column',
	alignItems: 'flex-start',
	gap: '16px',
});

const headingStyles = css({
	margin: '0',
	fontFamily: wacToken('font.charlie.display'),
	fontWeight: '500',
	fontSize: '44px',
	lineHeight: '1.15',
	[media.below.sm]: {
		fontSize: '32px',
	},
});

const subheadStyles = css({
	margin: '0',
	fontSize: '17px',
	fontWeight: '400',
	lineHeight: '1.5',
	color: wacToken('color.extended.blue100'),
});

const searchSlotStyles = css({
	width: '100%',
	maxWidth: '100%',
	minWidth: '0',
	marginBlockStart: '8px',
	display: 'flex',
	flexDirection: 'column',
	alignItems: 'stretch',
	justifyContent: 'flex-start',
	// The shared fuzzy-search renders <searchBarContainer><searchBar 965px>...</>.
	// Constrain both the container (direct child) and the 965px searchBar
	// (grandchild) to the narrower left column, and clip any residual overflow.
	overflow: 'hidden',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- constrains the shared fuzzy-search container to the narrower left column
	'& > *': {
		width: '100%',
		maxWidth: '100%',
		minWidth: '0',
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- constrains the shared fuzzy-search's hardcoded 965px searchBar (grandchild) to the column width
	'& > * > *': {
		width: '100%',
		maxWidth: '100%',
		minWidth: '0',
	},
});

const searchHintStyles = css({
	fontSize: '14px',
	fontWeight: '400',
	color: wacToken('color.extended.blue100'),
});

const rightColumnStyles = css({
	flex: '0 0 420px',
	display: 'flex',
	[media.below.lg]: {
		flex: '0 0 360px',
	},
	[media.below.sm]: {
		display: 'none',
	},
});

const cardStyles = css({
	display: 'flex',
	flexDirection: 'column',
	gap: '12px',
	width: '100%',
	backgroundColor: wacToken('color.neutral.light0'),
	borderRadius: '12px',
	padding: '24px',
	boxShadow: token('elevation.shadow.raised'),
});

const cardHeadlineStyles = css({
	margin: '0',
	fontFamily: wacToken('font.charlie.display'),
	fontSize: '22px',
	fontWeight: '500',
	lineHeight: '1.25',
	color: wacToken('color.primary.black'),
});

const cardStepsStyles = css({
	listStyle: 'none',
	margin: '0',
	padding: '0',
	display: 'flex',
	flexDirection: 'column',
	gap: '10px',
});

const cardStepStyles = css({
	display: 'flex',
	alignItems: 'center',
	gap: '12px',
	fontSize: '14px',
	fontWeight: '400',
	lineHeight: '1.4',
	color: wacToken('color.neutral.light700'),
});

const cardStepNumberStyles = css({
	flexShrink: 0,
	width: '24px',
	height: '24px',
	borderRadius: '50%',
	backgroundColor: wacToken('color.extended.blue100'),
	color: wacToken('color.primary.blue'),
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
	fontSize: '13px',
	fontWeight: '600',
});

const cardCtaContainerStyles = css({
	display: 'flex',
	alignItems: 'center',
	gap: '16px',
	marginBlockStart: '4px',
	flexWrap: 'wrap',
});

const primaryCtaStyles = css({
	[media.below.sm]: {
		width: '100%',
	},
});
